import { useLanguage } from '@/hooks/useLanguage';
import { tx as translateInterfaceText } from '@/utils/i18n';

import { useState, useEffect, useCallback, useRef } from 'react';
import { SalesNotification } from '@/store/videoFeatures';

interface UseSalesNotificationProps {
  salesNotification?: SalesNotification;
  forceShow?: boolean;
}

export const useSalesNotification = ({ 
  salesNotification, 
  forceShow = false 
}: UseSalesNotificationProps) => {
  useLanguage();
  const [visible, setVisible] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  const [exiting, setExiting] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  
  // Use refs for indices to avoid dependency loops
  const nameIndexRef = useRef(0);
  const cityIndexRef = useRef(0);
  const timersRef = useRef<NodeJS.Timeout[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isShowingRef = useRef(false);
  
  // Stable reference to salesNotification
  const configRef = useRef(salesNotification);
  configRef.current = salesNotification;
  
  // Cleanup all timers
  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);
  
  // Generate a message without triggering re-renders
  const generateMessage = useCallback((): string => {
    const config = configRef.current;
    if (!config) return '';
    
    const template = config.template || '';
    if (template.trim() === '') return '';
    
    const names = config.names || [];
    const cities = config.cities || [];
    
    const hasNomeVar = template.includes('{nome}') || template.includes('{Nome}');
    const hasCidadeVar = template.includes('{cidade}') || template.includes('{Cidade}');
    const hasProdutoVar = template.includes('{produto}') || template.includes('{Produto}');
    
    if (!hasNomeVar && !hasCidadeVar && !hasProdutoVar) {
      return template;
    }
    
    let message = template;
    
    if (hasNomeVar) {
      if (names.length > 0) {
        const name = names[nameIndexRef.current % names.length];
        message = message.replace(/{nome}/gi, name).replace(/{Nome}/g, name);
        nameIndexRef.current = (nameIndexRef.current + 1) % names.length;
      } else {
        message = message.replace(/{nome}/gi, '[Nome]').replace(/{Nome}/g, '[Nome]');
      }
    }
    
    if (hasCidadeVar) {
      if (cities.length > 0) {
        const city = cities[cityIndexRef.current % cities.length];
        message = message.replace(/{cidade}/gi, city).replace(/{Cidade}/g, city);
        cityIndexRef.current = (cityIndexRef.current + 1) % cities.length;
      } else {
        message = message.replace(/{cidade}/gi, '[Cidade]').replace(/{Cidade}/g, '[Cidade]');
      }
    }
    
    if (hasProdutoVar) {
      if (config.product) {
        message = message.replace(/{produto}/gi, config.product).replace(/{Produto}/g, config.product);
      } else {
        message = message.replace(/{produto}/gi, '[Produto]').replace(/{Produto}/g, '[Produto]');
      }
    }
    
    return message;
  }, []); // No dependencies - uses refs
  
  // Show a single notification cycle
  const showNotification = useCallback(() => {
    const config = configRef.current;
    if (isShowingRef.current || !config?.enabled) return;
    
    isShowingRef.current = true;
    
    const message = generateMessage();
    const finalMessage = message && message.trim() !== '' ? message : translateInterfaceText("Nova venda!");
    
    setCurrentMessage(finalMessage);
    setExiting(false);
    setVisible(true);
    setNotificationCount(prev => prev + 1);
    
    const duration = (config.duration || 5) * 1000;
    
    // Exit animation
    const exitTimer = setTimeout(() => {
      setExiting(true);
      
      // Hide after animation
      const hideTimer = setTimeout(() => {
        setVisible(false);
        setExiting(false);
        isShowingRef.current = false;
      }, 400);
      timersRef.current.push(hideTimer);
    }, duration);
    timersRef.current.push(exitTimer);
  }, [generateMessage]);
  
  // Force show for preview - stable, no re-animation on config changes
  useEffect(() => {
    if (!forceShow || !salesNotification?.enabled) {
      if (forceShow && !salesNotification?.enabled) {
        setVisible(false);
      }
      return;
    }
    
    const message = generateMessage();
    const finalMessage = message && message.trim() !== '' ? message : translateInterfaceText("Nova venda!");
    setCurrentMessage(finalMessage);
    setVisible(true);
    setExiting(false);
    isShowingRef.current = true;
  }, [
    forceShow, 
    salesNotification?.enabled,
    salesNotification?.template,
    salesNotification?.names,
    salesNotification?.cities,
    salesNotification?.product,
    salesNotification?.platform,
    salesNotification?.showIcon,
    salesNotification?.fontSize,
    salesNotification?.iconSize,
    generateMessage
  ]);
  
  // Set up notification loop (non-preview mode only)
  useEffect(() => {
    if (forceShow) return; // Preview handles its own display
    if (!salesNotification?.enabled) {
      clearAllTimers();
      return;
    }
    
    const startDelay = (salesNotification.startDelay || 0) * 1000;
    const frequency = (salesNotification.frequency || 30) * 1000;
    
    // Initial notification
    const initialTimer = setTimeout(() => {
      showNotification();
      
      // Set up interval for subsequent notifications
      intervalRef.current = setInterval(() => {
        if (!configRef.current?.loop && 
            configRef.current?.showCount && 
            notificationCount >= configRef.current.showCount) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return;
        }
        showNotification();
      }, frequency);
    }, startDelay);
    timersRef.current.push(initialTimer);
    
    return clearAllTimers;
  }, [salesNotification?.enabled, salesNotification?.frequency, salesNotification?.startDelay, salesNotification?.loop, salesNotification?.showCount, forceShow, showNotification, clearAllTimers, notificationCount]);
  
  return {
    visible,
    exiting,
    currentMessage,
    showNotification,
    notificationCount
  };
};
